/* ============================================================================
   FrameLogger — métriques perf live (Phase B6).

   Track FPS + frame time (avec décomposition par stage) + heap MB.
   Sample sur 60 frames glissantes pour stabilité. Expose statistiques
   p50/p95/p99 sur `window.__marthe_perf` en dev.

   Usage :
     const logger = createFrameLogger();
     // dans le RAF tick :
     logger.beginFrame(now);
     logger.sample('inference', 12.3);
     logger.sample('fluid', 1.8);
     logger.endFrame(now);

   Activation :
     - Toujours actif en dev (import.meta.env.DEV)
     - En prod : seulement si ?perf=1 dans l'URL
   ============================================================================ */

const SAMPLE_WINDOW = 60;
type Stage = 'inference' | 'fluid' | 'dyeToAscii' | 'paint' | string;

export interface FrameStats {
  fps: number;
  frameMs: { p50: number; p95: number; p99: number; current: number };
  stages: Record<Stage, { p50: number; p95: number; p99: number; current: number }>;
  heapMB: number | null;
  droppedFrames: number;
  totalFrames: number;
}

export interface FrameLogger {
  beginFrame(now: number): void;
  sample(stage: Stage, durationMs: number): void;
  endFrame(now: number): void;
  getStats(): FrameStats;
  enabled: boolean;
}

interface RingBuffer {
  values: Float32Array;
  index: number;
  full: boolean;
}

function createRingBuffer(size: number): RingBuffer {
  return { values: new Float32Array(size), index: 0, full: false };
}

function pushRing(rb: RingBuffer, v: number): void {
  rb.values[rb.index] = v;
  rb.index = (rb.index + 1) % rb.values.length;
  if (rb.index === 0) rb.full = true;
}

function getRingPercentile(rb: RingBuffer, pct: number): number {
  const len = rb.full ? rb.values.length : rb.index;
  if (len === 0) return 0;
  const sorted = Array.from(rb.values.subarray(0, len)).sort((a, b) => a - b);
  const idx = Math.min(len - 1, Math.max(0, Math.floor(pct * len)));
  return sorted[idx]!;
}

function isPerfEnabled(): boolean {
  if (typeof window === 'undefined') return false;
  // Dev mode (Vite injecte import.meta.env.DEV → true en `vite` / false en `vite build`)
  // En production : seulement si ?perf=1
  try {
    const meta = import.meta as unknown as { env?: { DEV?: boolean } };
    if (meta.env?.DEV) return true;
  } catch {
    // import.meta non dispo (env non-Vite) → fallback URL param
  }
  if (window.location && window.location.search.includes('perf=1')) return true;
  return false;
}

export function createFrameLogger(): FrameLogger {
  const enabled = isPerfEnabled();

  if (!enabled) {
    // No-op shim pour zero overhead en prod
    const noop = (): void => {};
    return {
      enabled: false,
      beginFrame: noop,
      sample: noop,
      endFrame: noop,
      getStats: () => ({
        fps: 0,
        frameMs: { p50: 0, p95: 0, p99: 0, current: 0 },
        stages: {},
        heapMB: null,
        droppedFrames: 0,
        totalFrames: 0,
      }),
    };
  }

  const frameMsBuffer = createRingBuffer(SAMPLE_WINDOW);
  const stageBuffers: Record<Stage, RingBuffer> = {};
  let lastFrameStartTs = 0;
  let frameStartTs = 0;
  let totalFrames = 0;
  let droppedFrames = 0;
  let heapMB: number | null = null;
  let lastHeapSampleTs = 0;
  let currentStages: Record<Stage, number> = {};

  const logger: FrameLogger = {
    enabled: true,

    beginFrame(now: number): void {
      frameStartTs = now;
      currentStages = {};
      // Sample heap au max 1×/sec (lecture coûteuse)
      const perfMem = (performance as unknown as { memory?: { usedJSHeapSize: number } }).memory;
      if (perfMem && now - lastHeapSampleTs > 1000) {
        heapMB = perfMem.usedJSHeapSize / (1024 * 1024);
        lastHeapSampleTs = now;
      }
    },

    sample(stage: Stage, durationMs: number): void {
      currentStages[stage] = (currentStages[stage] ?? 0) + durationMs;
      let buf = stageBuffers[stage];
      if (!buf) {
        buf = createRingBuffer(SAMPLE_WINDOW);
        stageBuffers[stage] = buf;
      }
      pushRing(buf, durationMs);
    },

    endFrame(now: number): void {
      const frameMs = now - frameStartTs;
      pushRing(frameMsBuffer, frameMs);
      totalFrames++;
      // Dropped frame : > 33ms (target 30fps min, 60fps idéal)
      if (frameMs > 33) droppedFrames++;
      lastFrameStartTs = frameStartTs;
    },

    getStats(): FrameStats {
      // FPS = 1000 / median frame interval (plus stable que count/sec)
      const p50 = getRingPercentile(frameMsBuffer, 0.5);
      const fps = p50 > 0 ? Math.round(1000 / p50) : 0;
      const stages: FrameStats['stages'] = {};
      for (const [name, buf] of Object.entries(stageBuffers)) {
        stages[name] = {
          p50: getRingPercentile(buf, 0.5),
          p95: getRingPercentile(buf, 0.95),
          p99: getRingPercentile(buf, 0.99),
          current: currentStages[name] ?? 0,
        };
      }
      return {
        fps,
        frameMs: {
          p50,
          p95: getRingPercentile(frameMsBuffer, 0.95),
          p99: getRingPercentile(frameMsBuffer, 0.99),
          current: lastFrameStartTs ? frameStartTs - lastFrameStartTs : 0,
        },
        stages,
        heapMB,
        droppedFrames,
        totalFrames,
      };
    },
  };

  // Expose pour debug (uniquement si enabled)
  if (typeof window !== 'undefined') {
    (window as unknown as { __marthe_perf?: () => FrameStats }).__marthe_perf = () =>
      logger.getStats();
  }

  return logger;
}
